import { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import Rosecity1 from '../assets/roseCity1.png';
import RoseCity2 from '../assets/roseCity.png';

const AboutGlobalStyle = createGlobalStyle`
  .about-page {
    background: linear-gradient(180deg, #0f0f1a 0%, #1a1034 100%);
    min-height: 100vh;
    padding-top: 80px;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 5px 15px rgba(108, 40, 217, 0.3); }
  50% { transform: scale(1.05); box-shadow: 0 10px 25px rgba(108, 40, 217, 0.5); }
  100% { transform: scale(1); box-shadow: 0 5px 15px rgba(108, 40, 217, 0.3); }
`;

const AboutContainer = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  overflow: hidden;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 4rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const AnimatedTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #ff4d8d, #6c28d9, #00c6fb);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientShift} 5s ease infinite;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const DecorativeCircle = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.1), rgba(255, 77, 141, 0.1));
  filter: blur(60px);
  z-index: -1;
  animation: ${float} 8s infinite ease-in-out;
  
  &:nth-child(1) {
    top: -100px;
    left: -100px;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    bottom: -100px;
    right: -100px;
    animation-delay: 2s;
  }
  
  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
  }
`;

const ContentSection = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(45deg, #ff4d8d, #6c28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    color: #e2e8f0;
    animation: ${fadeIn} 1s ease-out;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const VisualContent = styled(motion.div)`
  position: relative;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 968px) {
    height: 300px;
    order: -1;
  }
`;

const VisualImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
`;

const PartnerCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
    background: rgba(255, 255, 255, 0.1);
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: #cbd5e1;
    line-height: 1.6;
  }
`;

const FounderSection = styled(motion.div)`
  margin: 4rem 0;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const FounderDecorative = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.2), rgba(255, 77, 141, 0.2));
  filter: blur(40px);
  z-index: -1;
  
  &:nth-child(1) {
    top: -50px;
    left: -50px;
  }
  
  &:nth-child(2) {
    bottom: -50px;
    right: -50px;
  }
`;

const FounderText = styled.div`
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #e2e8f0;
    margin-bottom: 1.5rem;
    animation: ${fadeIn} 1s ease-out;
  }
  
  strong {
    background: linear-gradient(45deg, #ff4d8d, #6c28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-size: 1.3rem;
  }
`;

const SocialLinksContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
`;

const SocialLink = styled(motion.a)`
  color: #e2e8f0;
  text-decoration: none;
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.2), rgba(255, 77, 141, 0.2));
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(45deg, rgba(108, 40, 217, 0.4), rgba(255, 77, 141, 0.4));
    transform: translateY(-2px);
  }
`;

const SocialTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ff4d8d, #6c28d9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const EventList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
  text-align: left;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const EventItem = styled.li`
  font-size: 1.1rem;
  color: #e2e8f0;
  margin-bottom: 1rem;
  animation: ${fadeIn} 1s ease-out;
  position: relative;
  padding-left: 1.5rem;
  
  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: #ff4d8d;
    font-size: 1.5rem;
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: linear-gradient(45deg, #6c28d9, #ff4d8d);
  color: white;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(108, 40, 217, 0.3);
  animation: ${pulse} 2s infinite ease-in-out;
  border: none;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 25px rgba(108, 40, 217, 0.4);
  }
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 3rem 0;
`;

function About() {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [0, 1]);
  const scale = useTransform(scrollY, [0, 300], [0.8, 1]);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="about-page">
      <AboutGlobalStyle />
      <AboutContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <DecorativeCircle />
        <DecorativeCircle />
        
        <HeroSection>
          <AnimatedTitle
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            About RoseCity Web3
          </AnimatedTitle>
        </HeroSection>

        <ContentSection
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <TextContent>
            <h2>Our Story</h2>
            <p>
              RoseCity Web3 is a dynamic art collective based in Portland, Oregon, USA. We are passionate about integrating blockchain technology with creative expression, promoting brand & personal growth, opportunities to create community and drive the safe adoption of Web3 technologies!
            </p>
            <p>
              Partnered with ModMedia Network and championed by Metamu, we are boots on the ground in Oregon's largest city, hosting in-real-life (IRL) events and outreach programs to inspire and educate everyday citizens about the transformative potential of Web3 and cryptocurrency.
            </p>
          </TextContent>
          <VisualContent
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <VisualImage src={RoseCity2} alt="RoseCity Web3 events and community" />
          </VisualContent>
        </ContentSection>

        <ContentSection
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <VisualContent
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <VisualImage src={Rosecity1} alt="RoseCity Web3 mission" />
          </VisualContent>
          <TextContent>
            <h2>Our Mission</h2>
            <p>
              Our mission is to empower artists, creators, and communities by providing platforms for innovation, collaboration, and education. Through our events, we showcase cutting-edge digital art, connect creators with collectors, and foster a vibrant Web3 ecosystem.
            </p>
            <p>
              We believe in the power of blockchain technology to revolutionize creative industries and are committed to making this technology accessible to all.
            </p>
          </TextContent>
        </ContentSection>

        <motion.h2 
          style={{ textAlign: 'center', margin: '4rem 0 2rem' }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          Our Partners & Champions
        </motion.h2>

        <PartnerGrid>
          <PartnerCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h3>ModMedia Network</h3>
            <p>Our strategic partner in bringing Web3 education and events to the Portland community through innovative media solutions.</p>
          </PartnerCard>
          
          <PartnerCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h3>Metamu</h3>
            <p>Our champion Founder that provides the tools and infrastructure for artists to showcase and monetize their digital creations.</p>
          </PartnerCard>
          
          <PartnerCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h3>Community</h3>
            <p>The heart of RoseCity Web3 - artists, creators, and enthusiasts who make our events and initiatives possible.</p>
          </PartnerCard>
        </PartnerGrid>

        <FounderSection
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <FounderDecorative />
          <FounderDecorative />
          <AnimatedTitle
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.6 }}
          >
            Meet the Founder
          </AnimatedTitle>
          <FounderText>
            <p>Hi, my name is MetaMu and I am the founder of RoseCity Web3! We do workshops and events around crypto education!</p>
            <p><strong>🚨STAY TUNED‼️</strong><br />Talk Crypto PDX<br />(Beginner conversations and workshops for the Crypto curious 🤔)</p>
            <EventList>
              <EventItem>September - Soft Launch Fri. SEPT 26TH</EventItem>
              <EventItem>October - Grand Opening Celebration Sat. OCT 25th 2-5pm</EventItem>
              <EventItem>November - Film3 Portland Sat. NOV 29th</EventItem>
              <EventItem>December - RoseCity Nouns DAO drop party Sat. DEC 27th</EventItem>
            </EventList>
            <p>@Lloyd Center Mall AfroVillage Storefront #998</p>
            <SocialLinksContainer>
              <div>
                <SocialTitle>Personal Links</SocialTitle>
                <SocialLinks>
                  <SocialLink href="https://x.com/MetaM00" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>X: @MetaM00</SocialLink>
                  <SocialLink href="https://www.instagram.com/m3tamu/" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>Instagram: m3tamu</SocialLink>
                  <SocialLink href="https://www.tiktok.com/@metamu" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>TikTok: @metamu</SocialLink>
                  <SocialLink href="mailto:metamu503@gmail.com" whileHover={{ scale: 1.05 }}>Email: metamu503@gmail.com</SocialLink>
                </SocialLinks>
              </div>
              <div>
                <SocialTitle>RoseCity Web3 Links</SocialTitle>
                <SocialLinks>
                  <SocialLink href="https://farcaster.xyz/rosecityweb3" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>Farcaster: rosecityweb3</SocialLink>
                  <SocialLink href="https://farcaster.xyz/~/channel/rosecitynouns" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>Farcaster Channel: rosecitynouns</SocialLink>
                  <SocialLink href="https://x.com/RoseCityWeb3" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>X: @RoseCityWeb3</SocialLink>
                  <SocialLink href="https://www.tiktok.com/@rosecityweb3" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>TikTok: @rosecityweb3</SocialLink>
                  <SocialLink href="https://www.instagram.com/rosecity_web3" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }}>Instagram: rosecity_web3</SocialLink>
                </SocialLinks>
              </div>
            </SocialLinksContainer>
          </FounderText>
        </FounderSection>

        <ButtonContainer>
          <CTAButton
            href="https://www.canva.com/design/DAGyh3JN9rc/xMarmIX5CYqLL5geTlJyaw/view"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View full Presentation here!
          </CTAButton>
        </ButtonContainer>
      </AboutContainer>
    </div>
  );
}

export default About;
